import React, { useEffect, useState } from "react";
import { FaCalendarCheck, FaTimes, FaCheck, FaInfoCircle } from "react-icons/fa";
import { getSubs, updateSubscriptionStatus } from "../../utils/sub";
import { toast } from "sonner";

const statusOptions = ["All", "Active", "Pending", "Expired", "Cancelled", "Rejected"];

const statusColors = {
    Active: "bg-green-100 text-green-800 border-green-300",
    Pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
    Expired: "bg-red-100 text-red-800 border-red-300",
    Cancelled: "bg-gray-100 text-gray-800 border-gray-300",
    Rejected: "bg-red-50 text-red-700 border-red-200"
};

const PLAN_PRICES = {
    Premium: { Monthly: 3500, Annual: 35000 },
    "Premium Plus": { Monthly: 5000, Annual: 55000 },
};


const SubscriptionsPage = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [updating, setUpdating] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [showRejectSection, setShowRejectSection] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectNotes, setRejectNotes] = useState("");
    const [showRejectConfirm, setShowRejectConfirm] = useState(false);

    const rejectReasons = [
        "Invalid payment proof",
        "Amount does not match plan",
        "Fake or tampered screenshot",
        "User requested cancellation",
        "Payment not verified by bank",
        "Insufficient fund for plan",
        "Existing plan ongoing, applying it for your next cycle",
        "Other"
    ];


    useEffect(() => {
        const loadSubscriptions = async () => {
            try {
                const data = await getSubs();
                setSubscriptions(data?.subscriptions || []);
                console.log(data);
            } catch (err) {
                setError("Failed to fetch subscriptions. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        loadSubscriptions();
    }, []);

    const handleRowClick = (subscription) => {
        setSelectedSubscription({
            ...subscription,
            originalStatus: subscription.status,
            status: subscription.status, // ensure status is accurate
        });
        setRejectReason(subscription.reject_reason || "");
        setRejectNotes(subscription.reject_notes || "");
        setShowConfirmation(false);
    };


    const handleStatusChange = async () => {
        if (selectedSubscription.status === "Rejected" && !rejectReason) {
            toast.error("Please select a reason for rejection");
            return;
        }

        setUpdating(true);
        console.log(selectedSubscription.status)
        try {
            await updateSubscriptionStatus(selectedSubscription.subscription_id, {
                status: selectedSubscription.status,
                plan_type: selectedSubscription.plan_type,
                billing_cycle: selectedSubscription.billing_cycle,
                amount: selectedSubscription.amount,
                expiry_date: selectedSubscription.expiry_date,
                narration: selectedSubscription.narration,
                reject_reason: selectedSubscription.status === "Rejected" ? rejectReason : null,
                reject_notes: selectedSubscription.status === "Rejected" ? rejectNotes : null,

            });

            // ✅ Update subscriptions in state
            setSubscriptions((prevSubs) =>
                prevSubs.map((sub) =>
                    sub.subscription_id === selectedSubscription.subscription_id
                        ? { ...sub, ...selectedSubscription } // merge updated subscription
                        : sub
                )
            );

            // ✅ Close modal
            setSelectedSubscription(null);
            setShowConfirmation(false);
            setRejectReason("");
            setRejectNotes("");
        } catch (error) {
            console.error(error);
        }

        setUpdating(false);
    };



    const handleConfirmClick = () => {
        if (selectedSubscription.status !== selectedSubscription.originalStatus) {
            setShowConfirmation(true);
        }
    };

    useEffect(() => {
        if (
            selectedSubscription?.plan_type &&
            selectedSubscription?.billing_cycle &&
            selectedSubscription?.createdAt
        ) {
            // Update amount
            const newAmount =
                PLAN_PRICES[selectedSubscription.plan_type]?.[selectedSubscription.billing_cycle] || 0;

            // Calculate new expiry date from createdAt
            const createdDate = new Date(selectedSubscription.createdAt);
            let newExpiryDate = new Date(createdDate);

            if (selectedSubscription.billing_cycle === "Monthly") {
                newExpiryDate.setMonth(newExpiryDate.getMonth() + 1);
            } else if (selectedSubscription.billing_cycle === "Annual") {
                newExpiryDate.setFullYear(newExpiryDate.getFullYear() + 1);
            }

            // Add 3 extra days
            newExpiryDate.setDate(newExpiryDate.getDate() + 3);

            setSelectedSubscription((prev) => ({
                ...prev,
                amount: newAmount,
                expiry_date: newExpiryDate.toISOString().split("T")[0],
            }));
        }
    }, [selectedSubscription?.plan_type, selectedSubscription?.billing_cycle, selectedSubscription?.createdAt]);



    const PaginationButton = ({ direction, disabled, onClick }) => (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${disabled
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600 shadow-sm hover:shadow-md"
                }`}
            aria-label={`Go to ${direction} page`}
        >
            {direction === "prev" ? "← Previous" : "Next →"}
        </button>
    );

    const handlePageChange = (direction) => {
        setCurrentPage((prev) => {
            const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage);
            if (direction === "next" && prev < totalPages) return prev + 1;
            if (direction === "prev" && prev > 1) return prev - 1;
            return prev;
        });
    };

    const filteredSubscriptions = subscriptions.filter(
        (sub) =>
            (filterStatus === "All" || sub?.status === filterStatus) &&
            (
                String(sub?.subscription_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
                (sub?.fullName && sub.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (sub?.reference_number && sub.reference_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (sub?.teechaa_ref && sub.teechaa_ref.toLowerCase().includes(searchTerm.toLowerCase()))
            )
    );


    const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage);
    const displayedSubscriptions = filteredSubscriptions.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const hasStatusChanged = selectedSubscription &&
        selectedSubscription.status !== selectedSubscription.originalStatus;

    return (
        <div>
            <div className="max-w-7xl mx-auto">
                <h1 className="text-2xl sm:text-4xl font-bold text-blue-600 mb-6 sm:mb-8 text-center">
                    User Subscriptions
                </h1>

                {/* Filters */}
                <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex flex-col sm:flex-row gap-4 justify-between items-center">
                    <input
                        type="text"
                        placeholder="Search by ID, Reference, or User..."
                        className="border border-gray-300 px-4 py-2 rounded-lg w-full sm:w-1/2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select
                        className="border border-gray-300 px-4 py-2 rounded-lg w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        {statusOptions.map((status) => (
                            <option key={status} value={status}>
                                {status}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Table */}
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                            <p className="mt-4 text-blue-600 font-medium">Loading subscriptions...</p>
                        </div>
                    ) : error ? (
                        <p className="p-12 text-center text-red-600 font-medium">{error}</p>
                    ) : displayedSubscriptions.length === 0 ? (
                        <p className="p-12 text-center text-gray-500">No subscriptions found.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                                    <tr>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left font-semibold">Reference ID</th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left font-semibold">User</th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left font-semibold">Status</th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left font-semibold">Created Date</th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left font-semibold">Expiry Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {displayedSubscriptions.map((sub) => (
                                        <tr
                                            key={sub.subscription_id}
                                            className="hover:bg-blue-50 cursor-pointer transition-colors duration-150"
                                            onClick={() => handleRowClick(sub)}
                                        >
                                            <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-700 font-medium">
                                                {sub.reference_number}
                                            </td>
                                            <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-700">{sub.fullName}</td>
                                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusColors[sub.status] || 'bg-gray-100 text-gray-800'}`}>
                                                    {sub.status}
                                                </span>
                                            </td>
                                            <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-700">
                                                {new Date(sub.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-700">
                                                {new Date(sub.expiry_date).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!loading && !error && filteredSubscriptions.length > 0 && (
                    <div className="flex justify-between items-center mt-6">
                        <PaginationButton
                            direction="prev"
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange("prev")}
                        />
                        <span className="text-gray-700 font-medium bg-white px-4 py-2 rounded-lg shadow-sm">
                            Page {currentPage} of {totalPages}
                        </span>
                        <PaginationButton
                            direction="next"
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange("next")}
                        />
                    </div>
                )}
            </div>

            {/* Selected Subscription Modal */}
            {selectedSubscription && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative animate-fadeIn">
                        {/* Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-t-2xl">
                            <button
                                onClick={() => {
                                    setSelectedSubscription(null);
                                    setShowConfirmation(false);
                                }}
                                className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200"
                            >
                                <FaTimes size={20} />
                            </button>
                            <h2 className="text-2xl font-bold flex items-center gap-2">
                                <FaInfoCircle />
                                Subscription Details
                            </h2>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {/* Image */}
                            {selectedSubscription.image && (
                                <div className="mb-6">
                                    <img
                                        src={selectedSubscription.image}
                                        alt="Subscription Proof"
                                        className="w-full h-auto rounded-lg shadow-md border border-gray-200"
                                    />
                                </div>
                            )}

                            {/* Details Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">Reference ID</p>
                                    <p className="text-gray-900 font-semibold">
                                        {selectedSubscription.reference_number}
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">User</p>
                                    <p className="text-gray-900 font-semibold">
                                        {selectedSubscription.fullName}
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">Amount</p>
                                    <p className="text-gray-900 font-semibold">{parseFloat(selectedSubscription.amount).toLocaleString("en-NG", {
                                        style: "currency",
                                        currency: "NGN",
                                        minimumFractionDigits: 2, // optional, shows decimals
                                    })}

                                    </p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">Current Status</p>
                                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${statusColors[selectedSubscription.originalStatus]}`}>
                                        {selectedSubscription.originalStatus}
                                    </span>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">Created At</p>
                                    <p className="text-gray-900 font-semibold">
                                        {new Date(selectedSubscription.createdAt).toLocaleString()}
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">Expiry Date</p>
                                    <p className="text-gray-900 font-semibold">
                                        {new Date(selectedSubscription.expiry_date).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>

                            {/* Status Update Section */}
                            <div className="border-t border-gray-200 pt-6">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit Subscription Details</h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    {/* Plan Type */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Plan Type</label>
                                        <select
                                            value={selectedSubscription.plan_type}
                                            onChange={(e) =>
                                                setSelectedSubscription((prev) => ({ ...prev, plan_type: e.target.value }))
                                            }
                                            className="border border-gray-300 rounded-lg w-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="Premium">Premium</option>
                                            <option value="Premium Plus">Premium Plus</option>
                                        </select>
                                    </div>

                                    {/* Billing Cycle */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Billing Cycle</label>
                                        <select
                                            value={selectedSubscription.billing_cycle}
                                            onChange={(e) =>
                                                setSelectedSubscription((prev) => ({ ...prev, billing_cycle: e.target.value }))
                                            }
                                            className="border border-gray-300 rounded-lg w-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="Monthly">Monthly</option>
                                            <option value="Annual">Annual</option>
                                        </select>
                                    </div>

                                    {/* Amount */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Amount (₦)</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={selectedSubscription.amount?.toLocaleString("en-NG", {
                                                style: "currency",
                                                currency: "NGN",
                                                minimumFractionDigits: 2,
                                            })}
                                            className="border border-gray-300 rounded-lg w-full px-4 py-2 bg-gray-100 text-gray-700 cursor-not-allowed"
                                        />
                                    </div>


                                    {/* Expiry Date */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Expiry Date</label>
                                        <input
                                            type="date"
                                            value={selectedSubscription.expiry_date ? new Date(selectedSubscription.expiry_date).toISOString().split("T")[0] : ""}
                                            onChange={(e) =>
                                                setSelectedSubscription((prev) => ({ ...prev, expiry_date: e.target.value }))
                                            }
                                            className="border border-gray-300 rounded-lg w-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    {/* Narration */}
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Narration</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={selectedSubscription.narration}
                                            onChange={(e) =>
                                                setSelectedSubscription((prev) => ({ ...prev, narration: e.target.value }))
                                            }
                                            className="border border-gray-300 bg-gray-100 rounded-lg w-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

                                {/* Status Update */}
                                <label className="block text-gray-700 mb-2 font-semibold text-lg">Update Status</label>
                                <select
                                    value={selectedSubscription.status}
                                    onChange={(e) =>
                                        setSelectedSubscription((prev) => ({
                                            ...prev,
                                            status: e.target.value,
                                        }))
                                    }
                                    className="border border-gray-300 px-4 py-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
                                    disabled={updating}
                                >
                                    {statusOptions.filter((s) => s !== "All").map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                {selectedSubscription.status === "Rejected" && (
                                    <div className="mt-6 bg-red-50 border border-red-200 p-4 rounded-xl">
                                        <h3 className="text-lg font-semibold text-red-700 mb-4 flex items-center gap-2">
                                            Rejection Details
                                        </h3>

                                        {/* Reason Dropdown */}
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Reason for Rejection
                                            </label>
                                            <select
                                                value={rejectReason}
                                                onChange={(e) => {
                                                    const reason = e.target.value;
                                                    setRejectReason(reason);
                                                    setSelectedSubscription(prev => ({
                                                        ...prev,
                                                        reject_reason: reason
                                                    }));
                                                }}
                                                className="border border-gray-300 rounded-lg w-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                                            >
                                                <option value="">Select reason...</option>
                                                {rejectReasons.map((reason) => (
                                                    <option key={reason} value={reason}>
                                                        {reason}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Additional Notes */}
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Additional Notes (optional)
                                            </label>
                                            <textarea
                                                value={rejectNotes}
                                                onChange={(e) => {
                                                    const notes = e.target.value;
                                                    setRejectNotes(notes);
                                                    setSelectedSubscription(prev => ({
                                                        ...prev,
                                                        reject_notes: notes
                                                    }));
                                                }}
                                                className="border border-gray-300 rounded-lg w-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                                                placeholder="Add extra context if needed..."
                                            />
                                        </div>

                                        {/* Confirm Button */}
                                        <button
                                            onClick={() => {
                                                if (!rejectReason) {
                                                    toast.error("Please select a reason for rejection");
                                                    return;
                                                }
                                                setSelectedSubscription((prev) => ({
                                                    ...prev,
                                                    status: "Rejected"
                                                }));
                                                setShowRejectConfirm(true);
                                                setShowConfirmation(true);
                                            }}
                                            className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition duration-200 shadow-md font-medium"
                                        >
                                            Confirm Rejection
                                        </button>
                                    </div>
                                )}

                            </div>
                            {/* Confirmation Dialog */}
                            {showConfirmation && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                    <p className="text-blue-900 font-medium mb-3">
                                        Are you sure you want to update this subscription?
                                    </p>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleStatusChange}
                                            disabled={updating}
                                            className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-200 shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {updating ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    Updating...
                                                </>
                                            ) : (
                                                <>
                                                    <FaCheck /> Yes, Confirm
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => setShowConfirmation(false)}
                                            disabled={updating}
                                            className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            {!showConfirmation && (
                                <div className="flex gap-3 mt-4">
                                    <button
                                        onClick={handleConfirmClick} // just opens confirmation dialog
                                        className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200 shadow-md flex items-center justify-center gap-2 font-medium"
                                    >
                                        <FaCheck /> Update Subscription
                                    </button>
                                    <button
                                        onClick={() => setSelectedSubscription(null)}
                                        className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-md font-medium"
                                    >
                                        Close
                                    </button>
                                </div>
                            )}


                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscriptionsPage;